import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { SiteProvider } from "@/context/SiteContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileBar from "@/components/MobileBar";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

function ScrollManager() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) return; // hash scrolling handled by target pages
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

function PublicLayout() {
  return (
    <div className="pb-[52px] md:pb-0">
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <img src="/logo-mark-white.png" alt="Razor Build" className="w-14 animate-pulse" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <SiteProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollManager />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/renovations" element={<ServicePage slug="renovations" />} />
                <Route path="/extensions" element={<ServicePage slug="extensions" />} />
                <Route path="/new-builds" element={<ServicePage slug="new-builds" />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Legal page="privacy" />} />
                <Route path="/terms" element={<Legal page="terms" />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-center" richColors toastOptions={{ style: { borderRadius: 0, fontFamily: "Manrope, sans-serif" } }} />
      </SiteProvider>
    </div>
  );
}

export default App;
